// TODO: i18n — see Auth.js for pattern, replace hardcoded strings with t('namespace.key')
import { useState, useEffect, useRef } from 'react'
import { Camera, X, Loader, Check, Image as ImageIcon } from 'lucide-react'
import { supabase } from '../supabase'
import { recordProvenance } from '../utils/photoProvenance'
import { convertHeicIfNeeded } from '../utils/convertHeicIfNeeded'
import { stripExif } from '../utils/stripExif'
import { compressImage } from '../imageUtils'
// Named-only import: the Room Photo flow no longer renders <RoomScan>
// directly (Room Photo reframe — wide photo is the canvas, no review screen).
// The helpers below are still used by the Edit Item Detail Photos flow.
// RoomScan default export remains exported for ScanRoom's preserved-future
// multi-frame redesign path.
import { mergeFillNullsFromSingleItemAi, summaryFromFilled } from './RoomScan'
import ScanRoom from './ScanRoom'
import ScanItem from './ScanItem'
import RoomPhotoCapture from './RoomPhotoCapture'
import RoomPhotoViewer from './RoomPhotoViewer'

const EDIT_DETAIL_PHOTOS_MAX = 10
const EDIT_DETAIL_SPIN_STYLE = `@keyframes roomdetail-edit-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`

const C = {
  bg: '#F2EDE4', card: '#FDFAF6', border: '#DDD4C6',
  amber: '#B8721F', amberLight: '#D4902A', brown: '#2C1D14',
  muted: '#9A7B68', success: '#2E6B4A', danger: '#B03020',
  dangerBg: '#FDECEA',
}
const f = 'Georgia, serif'
const fs = 'Calibri, sans-serif'

const inp = {
  width: '100%', background: C.bg, border: `1px solid ${C.border}`,
  borderRadius: '8px', padding: '9px 12px', fontSize: '16px',
  fontFamily: fs, color: C.brown, boxSizing: 'border-box', outline: 'none',
  WebkitUserSelect: 'text', userSelect: 'text', touchAction: 'manipulation',
  WebkitAppearance: 'none',
}
const lbl = {
  fontSize: '10px', color: C.muted, letterSpacing: '1.5px',
  textTransform: 'uppercase', fontFamily: fs, display: 'block', marginBottom: '4px',
}
const subHdr = {
  fontSize: '10px', color: C.muted, letterSpacing: '2px', textTransform: 'uppercase',
  fontFamily: fs, fontWeight: '700', marginBottom: '10px', marginTop: '24px',
}

const FLOOR_OPTIONS = [
  { value: 0, label: 'Basement' }, { value: 1, label: '1st Floor' },
  { value: 2, label: '2nd Floor' }, { value: 3, label: '3rd Floor' },
  { value: 4, label: '4th Floor' }, { value: 5, label: 'Attic' },
  { value: 99, label: 'Other' },
]
const ROOM_TYPES = [
  ['bedroom', 'Bedroom'], ['bathroom', 'Bathroom'], ['kitchen', 'Kitchen'],
  ['living', 'Living Room'], ['dining', 'Dining Room'], ['garage', 'Garage'],
  ['office', 'Office'], ['den', 'Den'], ['pantry', 'Pantry'],
  ['closet', 'Closet'], ['hallway', 'Hallway'], ['other', 'Other'],
]
const FLOORING_TYPES = ['Hardwood', 'Carpet', 'Tile', 'Vinyl / LVP', 'Laminate', 'Concrete', 'Other']
const FINISHES = ['Flat', 'Eggshell', 'Satin', 'Semi-Gloss', 'Gloss']
const INVENTORY_CATEGORIES = [
  'Furniture', 'Electronics', 'Appliances',
  'Housewares', 'Linens', 'Décor',
  'Bags & Luggage', 'Books & Media',
  'Jewelry', 'Art', 'Clothing', 'Tools',
  'Musical Instruments', 'Sports Equipment', 'Collectibles',
  'Other',
]

const getReorderUrl = (retailer, brand, colorName, colorCode) => {
  const query = encodeURIComponent(`${brand || ''} ${colorName || ''} ${colorCode || ''}`.trim())
  const urls = {
    'Sherwin-Williams': `https://www.sherwin-williams.com/search?q=${query}`,
    'Benjamin Moore': `https://www.benjaminmoore.com/en-us/color-overview/find-your-color?q=${query}`,
    'Behr': `https://www.behr.com/consumer/colors/paint/?q=${query}`,
  }
  return urls[retailer] || '#'
}

function ReorderLinks({ brand, color, code }) {
  if (!code && !brand) return null
  return (
    <div style={{ display: 'flex', gap: '8px', marginTop: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
      <span style={{ fontSize: '11px', color: C.muted, fontFamily: fs }}>🛒 Reorder:</span>
      {['Sherwin-Williams', 'Benjamin Moore', 'Behr'].map(retailer => (
        <a key={retailer} href={getReorderUrl(retailer, brand, color, code)} target="_blank" rel="noopener noreferrer"
          style={{ fontSize: '11px', color: C.amber, textDecoration: 'none', fontFamily: fs }}>
          {retailer} →
        </a>
      ))}
    </div>
  )
}

function SaveButton({ onSave, saving }) {
  return (
    <button
      onClick={onSave}
      disabled={saving}
      style={{ width: '100%', background: saving ? C.border : `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`, border: 'none', borderRadius: '10px', padding: '13px', color: saving ? C.muted : C.card, fontSize: '14px', fontWeight: '700', cursor: saving ? 'not-allowed' : 'pointer', fontFamily: f, letterSpacing: '1px', textTransform: 'uppercase', marginTop: '20px' }}
    >{saving ? 'Saving...' : 'Save'}</button>
  )
}

function RoomPaintSection({ room, updateRoom, saving, onSave }) {
  return (
    <div>
      <div style={subHdr}>Wall Paint</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '6px' }}>
        <div><label style={lbl}>Brand</label><input style={inp} value={room.wall_paint_brand || ''} onChange={e => updateRoom('wall_paint_brand', e.target.value)} placeholder="e.g. Sherwin-Williams" /></div>
        <div><label style={lbl}>Color Name</label><input style={inp} value={room.wall_paint_color || ''} onChange={e => updateRoom('wall_paint_color', e.target.value)} placeholder="e.g. Accessible Beige" /></div>
        <div><label style={lbl}>Color Code</label><input style={inp} value={room.wall_paint_code || ''} onChange={e => updateRoom('wall_paint_code', e.target.value)} placeholder="e.g. SW 7036" /></div>
        <div>
          <label style={lbl}>Finish</label>
          <select style={{ ...inp, appearance: 'none' }} value={room.wall_paint_finish || ''} onChange={e => updateRoom('wall_paint_finish', e.target.value)}>
            <option value="">Select...</option>
            {FINISHES.map(o => <option key={o} value={o.toLowerCase()}>{o}</option>)}
          </select>
        </div>
      </div>
      <ReorderLinks brand={room.wall_paint_brand} color={room.wall_paint_color} code={room.wall_paint_code} />

      <div style={subHdr}>Trim Paint</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '6px' }}>
        <div><label style={lbl}>Brand</label><input style={inp} value={room.trim_paint_brand || ''} onChange={e => updateRoom('trim_paint_brand', e.target.value)} placeholder="Brand" /></div>
        <div><label style={lbl}>Color Name</label><input style={inp} value={room.trim_paint_color || ''} onChange={e => updateRoom('trim_paint_color', e.target.value)} placeholder="Color" /></div>
        <div><label style={lbl}>Color Code</label><input style={inp} value={room.trim_paint_code || ''} onChange={e => updateRoom('trim_paint_code', e.target.value)} placeholder="Code" /></div>
      </div>
      <ReorderLinks brand={room.trim_paint_brand} color={room.trim_paint_color} code={room.trim_paint_code} />

      <div style={subHdr}>Ceiling Paint</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '6px' }}>
        <div><label style={lbl}>Brand</label><input style={inp} value={room.ceiling_paint_brand || ''} onChange={e => updateRoom('ceiling_paint_brand', e.target.value)} placeholder="Brand" /></div>
        <div><label style={lbl}>Color Name</label><input style={inp} value={room.ceiling_paint_color || ''} onChange={e => updateRoom('ceiling_paint_color', e.target.value)} placeholder="Color" /></div>
      </div>
      <SaveButton onSave={onSave} saving={saving} />
    </div>
  )
}

function RoomFlooringSection({ room, updateRoom, saving, onSave }) {
  return (
    <div>
      <div style={subHdr}>Flooring</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
        <div>
          <label style={lbl}>Type</label>
          <select style={{ ...inp, appearance: 'none' }} value={room.flooring_type || ''} onChange={e => updateRoom('flooring_type', e.target.value)}>
            <option value="">Select...</option>
            {FLOORING_TYPES.map(o => <option key={o} value={o.toLowerCase().replace(/ \/ /g, '_').replace(/ /g, '_')}>{o}</option>)}
          </select>
        </div>
        <div><label style={lbl}>Brand</label><input style={inp} value={room.flooring_brand || ''} onChange={e => updateRoom('flooring_brand', e.target.value)} placeholder="Brand" /></div>
        <div><label style={lbl}>Color / Style</label><input style={inp} value={room.flooring_color || ''} onChange={e => updateRoom('flooring_color', e.target.value)} placeholder="Color" /></div>
        <div><label style={lbl}>Model #</label><input style={inp} value={room.flooring_model || ''} onChange={e => updateRoom('flooring_model', e.target.value)} placeholder="Model" /></div>
        <div><label style={lbl}>Year Installed</label><input style={inp} type="number" value={room.flooring_year_installed || ''} onChange={e => updateRoom('flooring_year_installed', e.target.value)} placeholder="2018" /></div>
      </div>
      <SaveButton onSave={onSave} saving={saving} />
    </div>
  )
}

function RoomDimensionsSection({ room, updateRoom, saving, onSave }) {
  const [showPhotoMeasure, setShowPhotoMeasure] = useState(false)
  const [photoMeasureImage, setPhotoMeasureImage] = useState(null)
  const [estimating, setEstimating] = useState(false)
  const [estimatedDimensions, setEstimatedDimensions] = useState(null)

  const autoSqFt = room.dimension_length && room.dimension_width
    ? Math.round(parseFloat(room.dimension_length) * parseFloat(room.dimension_width))
    : null

  const handlePhotoMeasureUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = async (ev) => {
      const base64 = ev.target.result.split(',')[1]
      setPhotoMeasureImage(ev.target.result)
      setEstimating(true)
      try {
        const { data } = await supabase.functions.invoke('estimate-room-dimensions', {
          body: { image_base64: base64, room_name: room.name }
        })
        if (data && data.length && data.width) setEstimatedDimensions(data)
      } catch {}
      setEstimating(false)
    }
    reader.readAsDataURL(file)
  }

  return (
    <div>
      <div style={subHdr}>Dimensions</div>

      {/* Helper buttons */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
        <button
          onClick={() => window.open('measure://', '_blank')}
          style={{ flex: 1, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 0', cursor: 'pointer', fontSize: 13, color: C.brown, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontFamily: fs }}
        >
          📏 Open Measure App
        </button>
        <button
          onClick={() => setShowPhotoMeasure(true)}
          style={{ flex: 1, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 0', cursor: 'pointer', fontSize: 13, color: C.brown, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontFamily: fs }}
        >
          📷 Estimate from Photo
        </button>
      </div>

      <div style={{ fontSize: 11, color: C.muted, marginBottom: 12, textAlign: 'center', fontFamily: fs }}>Or enter dimensions manually below</div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <input style={{ ...inp, flex: 1 }} type="number" value={room.dimension_length || ''} onChange={e => updateRoom('dimension_length', e.target.value)} placeholder="Length" />
        <span style={{ color: C.muted, fontFamily: fs, flexShrink: 0 }}>×</span>
        <input style={{ ...inp, flex: 1 }} type="number" value={room.dimension_width || ''} onChange={e => updateRoom('dimension_width', e.target.value)} placeholder="Width" />
        <span style={{ color: C.muted, fontFamily: fs, flexShrink: 0, fontSize: '13px' }}>ft</span>
      </div>
      {autoSqFt && (
        <div style={{ background: C.bg, borderRadius: '8px', padding: '10px 14px', fontSize: '14px', color: C.muted, fontFamily: fs, marginBottom: '12px' }}>
          = {autoSqFt} sq ft (auto-calculated)
        </div>
      )}
      <label style={lbl}>Square Footage</label>
      <input style={inp} type="number" value={room.sq_footage || ''} onChange={e => updateRoom('sq_footage', e.target.value)} placeholder="e.g. 280" />
      <SaveButton onSave={onSave} saving={saving} />

      {/* Photo estimation modal */}
      {showPhotoMeasure && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-end' }}>
          <div style={{ background: C.card, borderRadius: '16px 16px 0 0', padding: 24, width: '100%', maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ fontFamily: f, fontSize: 18, color: C.brown, marginBottom: 8 }}>📷 Estimate from Photo</div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 20, lineHeight: 1.6, fontFamily: fs }}>
              Take a photo of the room from a corner. Include a door or standard furniture for reference. Claude will estimate the dimensions.
            </div>

            {!photoMeasureImage ? (
              <label style={{ display: 'block', width: '100%', background: C.bg, border: `2px dashed ${C.border}`, borderRadius: 10, padding: 32, textAlign: 'center', cursor: 'pointer', boxSizing: 'border-box' }}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>📷</div>
                <div style={{ fontSize: 14, color: C.muted, fontFamily: fs }}>Tap to take or upload a photo</div>
                <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handlePhotoMeasureUpload} />
              </label>
            ) : (
              <div>
                <img src={photoMeasureImage} alt="Room" style={{ width: '100%', borderRadius: 10, marginBottom: 16, maxHeight: 300, objectFit: 'cover' }} />
                {estimating ? (
                  <div style={{ textAlign: 'center', color: C.muted, padding: 20, fontFamily: fs }}>Analyzing room dimensions...</div>
                ) : estimatedDimensions ? (
                  <div style={{ background: C.bg, borderRadius: 10, padding: 16, marginBottom: 16 }}>
                    <div style={{ fontFamily: f, fontSize: 16, color: C.brown, marginBottom: 4 }}>Estimated dimensions:</div>
                    <div style={{ fontSize: 20, color: C.amber, marginBottom: 8 }}>{estimatedDimensions.length} × {estimatedDimensions.width} ft</div>
                    <div style={{ fontSize: 12, color: C.muted, fontFamily: fs }}>
                      Confidence: {estimatedDimensions.confidence}
                      {estimatedDimensions.confidence === 'low' && ' — consider measuring manually for accuracy'}
                    </div>
                  </div>
                ) : null}

                <div style={{ display: 'flex', gap: 8 }}>
                  {estimatedDimensions && (
                    <button
                      onClick={() => {
                        updateRoom('dimension_length', estimatedDimensions.length)
                        updateRoom('dimension_width', estimatedDimensions.width)
                        setShowPhotoMeasure(false)
                        setPhotoMeasureImage(null)
                        setEstimatedDimensions(null)
                      }}
                      style={{ flex: 2, background: C.amber, color: '#fff', border: 'none', borderRadius: 8, padding: '14px 0', fontFamily: f, fontSize: 15, cursor: 'pointer' }}
                    >
                      ✓ Use these dimensions
                    </button>
                  )}
                  <button
                    onClick={() => { setPhotoMeasureImage(null); setEstimatedDimensions(null) }}
                    style={{ flex: 1, background: 'none', border: `1px solid ${C.border}`, borderRadius: 8, padding: '14px 0', fontSize: 14, cursor: 'pointer', color: C.muted, fontFamily: fs }}
                  >
                    Retake
                  </button>
                </div>
              </div>
            )}

            <button
              onClick={() => { setShowPhotoMeasure(false); setPhotoMeasureImage(null); setEstimatedDimensions(null) }}
              style={{ width: '100%', background: 'none', border: 'none', color: C.muted, fontSize: 14, cursor: 'pointer', marginTop: 12, padding: 8, fontFamily: fs }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function RoomFixturesSection({ room, updateRoom, saving, onSave }) {
  return (
    <div>
      <div style={subHdr}>Fixtures & Lighting</div>
      <label style={lbl}>Notes</label>
      <textarea
        style={{ ...inp, resize: 'vertical', minHeight: '120px' }}
        value={room.fixtures_notes || ''}
        onChange={e => updateRoom('fixtures_notes', e.target.value)}
        placeholder="e.g. Recessed LED lights (6 total), ceiling fan, 3-way switches, dimmer on main circuit"
      />
      <SaveButton onSave={onSave} saving={saving} />
    </div>
  )
}

function RoomWindowsSection({ room, updateRoom, saving, onSave }) {
  return (
    <div>
      <div style={subHdr}>Windows & Doors</div>
      <label style={lbl}>Notes</label>
      <textarea
        style={{ ...inp, resize: 'vertical', minHeight: '120px' }}
        value={room.windows_notes || ''}
        onChange={e => updateRoom('windows_notes', e.target.value)}
        placeholder="e.g. 2 double-hung windows (Anderson 400 series, installed 2018), 6-panel door, sliding closet doors"
      />
      <SaveButton onSave={onSave} saving={saving} />
    </div>
  )
}

function RoomNotesSection({ room, updateRoom, saving, onSave }) {
  return (
    <div>
      <div style={subHdr}>Room Notes</div>
      <textarea
        style={{ ...inp, resize: 'vertical', minHeight: '160px' }}
        value={room.room_notes || ''}
        onChange={e => updateRoom('room_notes', e.target.value)}
        placeholder="Renovation history, special features, contractor notes, measurements, etc."
      />
      <SaveButton onSave={onSave} saving={saving} />
    </div>
  )
}

function RoomInventorySection({ room, homeId, onRoomUpdate }) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [editingItem, setEditingItem] = useState(null)
  const [noticeSeen] = useState(() => localStorage.getItem('inventory_notice_seen') === 'true')
  const [showNotice, setShowNotice] = useState(!noticeSeen)
  const [photos, setPhotos] = useState([])
  const [receipt, setReceipt] = useState(null)
  const [showVideoRecorder, setShowVideoRecorder] = useState(false)
  const [showRoomPhotoCapture, setShowRoomPhotoCapture] = useState(false)
  const [showScanRoom, setShowScanRoom] = useState(false)
  const [showScanItem, setShowScanItem] = useState(false)
  const [showCameraRollChooser, setShowCameraRollChooser] = useState(false)
  const [scanItemSource, setScanItemSource] = useState('camera') // 'camera' | 'library'
  const [roomPhotoSource, setRoomPhotoSource] = useState('camera') // 'camera' | 'library'
  const [videoSavedMessage, setVideoSavedMessage] = useState(false)
  const [form, setForm] = useState({
    name: '', category: '', brand: '', model_number: '', serial_number: '',
    manufactured: '',
    purchase_date: '', purchase_price: '', appraised_value: '', appraised_by: '',
    appraisal_date: '', notes: '',
  })
  const [saving, setSaving] = useState(false)

  // Edit-mode Detail Photos with AI extraction (Part B). Distinct from the
  // legacy in-modal `photos` state above (which captures local data URLs and
  // is never persisted). These are signed URLs uploaded to the room-scans
  // bucket; each new photo runs single_item AI extraction and fill-nulls-
  // merges into `form`. Only shown when editingItem is truthy.
  const [editPhotoUrls, setEditPhotoUrls] = useState([])
  const [editPhotoUploading, setEditPhotoUploading] = useState(false)
  const [editPhotoError, setEditPhotoError] = useState(null)
  const [editExtractEnabled, setEditExtractEnabled] = useState(true)
  // Keyed by signed photo URL: { state: 'extracting'|'success'|'error', summary?: string }
  const [editDetailExtractions, setEditDetailExtractions] = useState({})
  const editDetailCameraRef = useRef(null)
  const editDetailLibraryRef = useRef(null)

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0]
    if (!file) return
    if (photos.length >= 5) { alert('Maximum 5 photos per item'); return }
    const reader = new FileReader()
    reader.onload = (ev) => setPhotos(p => [...p, ev.target.result])
    reader.readAsDataURL(file)
    e.target.value = ''
  }

  const handleReceiptUpload = (e) => {
    const file = e.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => setReceipt(ev.target.result)
    reader.readAsDataURL(file)
    e.target.value = ''
  }

  useEffect(() => { loadItems() }, [room.id])

  const loadItems = async () => {
    setLoading(true)
    const { data } = await supabase.from('room_inventory').select('*').eq('room_id', room.id).order('created_at')
    setItems(data || [])
    setLoading(false)
  }

  const setField = (k, v) => setForm(p => ({ ...p, [k]: v }))

  const handleSave = async () => {
    if (!form.name.trim()) return
    setSaving(true)
    const payload = {
      room_id: room.id, home_id: homeId,
      name: form.name.trim(),
      category: form.category || null,
      brand: form.brand || null,
      model_number: form.model_number || null,
      serial_number: form.serial_number || null,
      manufactured: form.manufactured || null,
      purchase_date: form.purchase_date || null,
      purchase_price: form.purchase_price ? parseFloat(form.purchase_price) : null,
      appraised_value: form.appraised_value ? parseFloat(form.appraised_value) : null,
      appraised_by: form.appraised_by || null,
      appraisal_date: form.appraisal_date || null,
      notes: form.notes || null,
    }
    if (editingItem) {
      // Merge any Detail Photos added during the edit session into the
      // existing photo_urls array. Skip if no photos were touched (preserves
      // any photos managed outside this edit modal).
      const existingUrls = Array.isArray(editingItem.photo_urls) ? editingItem.photo_urls : []
      const sameAsExisting = editPhotoUrls.length === existingUrls.length
        && editPhotoUrls.every((u, i) => u === existingUrls[i])
      if (!sameAsExisting) {
        payload.photo_urls = editPhotoUrls.length > 0 ? editPhotoUrls : null
      }
      const { data } = await supabase.from('room_inventory').update(payload).eq('id', editingItem.id).select().single()
      if (data) setItems(prev => prev.map(i => i.id === data.id ? data : i))
    } else {
      const { data } = await supabase.from('room_inventory').insert(payload).select().single()
      if (data) setItems(prev => [...prev, data])
    }
    setSaving(false)
    setShowAdd(false)
    setEditingItem(null)
    setForm({ name: '', category: '', brand: '', model_number: '', serial_number: '', manufactured: '', purchase_date: '', purchase_price: '', appraised_value: '', appraised_by: '', appraisal_date: '', notes: '' })
    setEditPhotoUrls([])
    setEditDetailExtractions({})
    setEditPhotoError(null)
    setEditExtractEnabled(true)
  }

  const handleDelete = async (id) => {
    await supabase.from('room_inventory').delete().eq('id', id)
    setItems(prev => prev.filter(i => i.id !== id))
  }

  const openEdit = (item) => {
    setEditingItem(item)
    setForm({
      name: item.name || '', category: item.category || '', brand: item.brand || '',
      model_number: item.model_number || '', serial_number: item.serial_number || '',
      manufactured: item.manufactured || '',
      purchase_date: item.purchase_date || '', purchase_price: item.purchase_price || '',
      appraised_value: item.appraised_value || '', appraised_by: item.appraised_by || '',
      appraisal_date: item.appraisal_date || '', notes: item.notes || '',
    })
    setEditPhotoUrls(Array.isArray(item.photo_urls) ? [...item.photo_urls] : [])
    setEditDetailExtractions({})
    setEditPhotoError(null)
    setEditExtractEnabled(true)
    setShowAdd(true)
  }

  // ----- Edit-mode Detail Photos handlers (Part B) --------------------------

  const handleEditDetailPhotoSelect = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !editingItem) return
    if (editPhotoUrls.length >= EDIT_DETAIL_PHOTOS_MAX) {
      setEditPhotoError(`You've reached the limit of ${EDIT_DETAIL_PHOTOS_MAX} photos per item.`)
      return
    }
    setEditPhotoUploading(true)
    setEditPhotoError(null)
    let processedForExtraction = null
    let signedUrlForExtraction = null
    try {
      const jpeg = await convertHeicIfNeeded(file)
      const stripped = await stripExif(jpeg)
      const processed = await compressImage(stripped, 1600, 0.80)
      processedForExtraction = processed
      const userResp = await supabase.auth.getUser()
      const userId = userResp?.data?.user?.id
      if (!userId) throw new Error('Could not confirm your account. Please retry.')
      const path = `${userId}/inventory/${editingItem.id}/${Date.now()}.jpg`
      const { error: upErr } = await supabase.storage
        .from('room-scans')
        .upload(path, processed, { contentType: 'image/jpeg', upsert: false })
      if (upErr) throw upErr
      const { data: signed, error: signErr } = await supabase.storage
        .from('room-scans')
        .createSignedUrl(path, 60 * 60 * 24 * 365)
      if (signErr || !signed?.signedUrl) throw new Error('Could not generate a link for the photo.')
      signedUrlForExtraction = signed.signedUrl
      setEditPhotoUrls(prev => [...prev, signed.signedUrl])
    } catch (err) {
      console.error('[RoomDetail edit] detail photo upload error', err)
      setEditPhotoError(err.message || 'Could not add that photo. Please try another.')
      setEditPhotoUploading(false)
      return
    }
    setEditPhotoUploading(false)

    if (editExtractEnabled && processedForExtraction && signedUrlForExtraction) {
      runEditDetailExtraction(signedUrlForExtraction, processedForExtraction)
    }
  }

  const runEditDetailExtraction = async (photoUrl, processedBlob) => {
    setEditDetailExtractions(prev => ({ ...prev, [photoUrl]: { state: 'extracting' } }))
    let base64
    try {
      base64 = await new Promise((resolve, reject) => {
        const r = new FileReader()
        r.onload = () => resolve(String(r.result).split(',')[1] || '')
        r.onerror = () => reject(new Error('FileReader failed'))
        r.readAsDataURL(processedBlob)
      })
    } catch (err) {
      console.error('[RoomDetail edit] extraction base64 error', err)
      setEditDetailExtractions(prev => ({ ...prev, [photoUrl]: { state: 'error' } }))
      return
    }
    try {
      const { data, error } = await supabase.functions.invoke('enumerate-room-items', {
        body: {
          capture_mode: 'single_item',
          photo_base64: base64,
          room_id: room.id,
          home_id: homeId,
          media_type: 'image/jpeg',
        },
      })
      if (error) throw new Error(error.message || 'function failed')
      const ai = data?.items?.[0]?.ai_details
      let filledFields = []
      setForm(prev => {
        const { next, filledFields: filled } = mergeFillNullsFromSingleItemAi(prev, ai)
        filledFields = filled
        return next
      })
      const summary = summaryFromFilled(filledFields)
      setEditDetailExtractions(prev => ({ ...prev, [photoUrl]: { state: 'success', summary } }))
      setTimeout(() => {
        setEditDetailExtractions(prev => {
          const next = { ...prev }
          delete next[photoUrl]
          return next
        })
      }, 2500)
    } catch (err) {
      console.error('[RoomDetail edit] extraction error', err)
      setEditDetailExtractions(prev => ({ ...prev, [photoUrl]: { state: 'error' } }))
    }
  }

  const removeEditDetailPhoto = (idx) => {
    const removedUrl = editPhotoUrls[idx]
    setEditPhotoUrls(prev => prev.filter((_, i) => i !== idx))
    if (removedUrl) {
      setEditDetailExtractions(prev => {
        if (!(removedUrl in prev)) return prev
        const next = { ...prev }
        delete next[removedUrl]
        return next
      })
    }
  }

  if (loading) return <div style={{ color: C.muted, fontFamily: fs, fontSize: '13px', padding: '20px 0' }}>Loading...</div>

  if (showVideoRecorder) {
    return (
      <div>
        <button onClick={() => setShowVideoRecorder(false)} style={{ background: 'none', border: 'none', color: C.amber, cursor: 'pointer', fontSize: '14px', padding: '0 0 16px', fontFamily: fs }}>← Back to Inventory</button>
        <RoomVideoSection
          room={room}
          homeId={homeId}
          onRoomUpdate={onRoomUpdate}
          onDone={() => { setShowVideoRecorder(false); setVideoSavedMessage(true) }}
        />
      </div>
    )
  }

  return (
    <div>
      {showNotice && (
        <div style={{ background: '#F0F7FF', border: '1px solid #B8D4F0', borderRadius: '10px', padding: '16px', marginBottom: '16px' }}>
          <div style={{ fontSize: '14px', color: '#1565C0', fontWeight: '600', marginBottom: '8px' }}>🔒 Your inventory is private</div>
          <div style={{ fontSize: '13px', color: C.brown, lineHeight: 1.6 }}>
            This section is visible only to home members. It is never shared with contractors or anyone else outside your home.
          </div>
          <button onClick={() => { setShowNotice(false); localStorage.setItem('inventory_notice_seen', 'true') }}
            style={{ background: C.amber, color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontSize: '13px', marginTop: '12px', fontFamily: fs }}>
            Got it
          </button>
        </div>
      )}

      {videoSavedMessage && (
        <div style={{ background: '#EBF5EF', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', fontSize: '13px', color: C.success, fontFamily: fs, lineHeight: 1.5 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <span>✓ Video saved. AI item extraction is coming soon — for now you can review your video anytime.</span>
            <button onClick={() => setVideoSavedMessage(false)} style={{ background: 'none', border: 'none', color: C.success, cursor: 'pointer', fontSize: '16px', padding: 0, marginLeft: '8px', lineHeight: 1, flexShrink: 0 }}>×</button>
          </div>
        </div>
      )}

      {!showAdd && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
          {/* Record Video tile (raw-video flow → rooms.video_urls) is hidden
              for now. The legacy code path (RoomVideoSection +
              setShowVideoRecorder) is preserved so it can be restored by
              un-hiding it.

              ScanRoom.js (video walkthrough + multi-photo) is hidden as of
              the v8 rollback. ScanRoom.js, showScanRoom state, and the
              conditional render below remain intact for the future
              multi-frame redesign. */}
          {/* 📷 Room Photo — wide-angle canvas. Photo is the artifact; AI
              detections land in the latent room_photo_detections table for
              future tap-to-identify. No review screen. */}
          <button
            onClick={() => { setRoomPhotoSource('camera'); setShowRoomPhotoCapture(true) }}
            style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#FBF3E8', border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px 16px', cursor: 'pointer', textAlign: 'left', width: '100%' }}
          >
            <span style={{ fontSize: '20px' }}>📷</span>
            <div>
              <div style={{ fontFamily: fs, fontSize: '14px', color: C.brown, fontWeight: '600' }}>Room Photo</div>
              <div style={{ fontFamily: fs, fontSize: '11px', color: C.muted, marginTop: '1px' }}>Wide-angle — captures the room as evidence</div>
            </div>
          </button>
          {/* 🔍 Item Photo — close-up, single-item deep identification. */}
          <button
            onClick={() => { setScanItemSource('camera'); setShowScanItem(true) }}
            style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#FBF3E8', border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px 16px', cursor: 'pointer', textAlign: 'left', width: '100%' }}
          >
            <span style={{ fontSize: '20px' }}>🔍</span>
            <div>
              <div style={{ fontFamily: fs, fontSize: '14px', color: C.brown, fontWeight: '600' }}>Item Photo</div>
              <div style={{ fontFamily: fs, fontSize: '11px', color: C.muted, marginTop: '1px' }}>Close-up — log one specific thing in detail</div>
            </div>
          </button>
          {/* 🖼 Add from Camera Roll — opens a chooser, then routes to the
              Room Photo or Item Photo flow with sourcePreference='library'. */}
          <button
            onClick={() => setShowCameraRollChooser(true)}
            style={{ display: 'flex', alignItems: 'center', gap: '12px', background: C.bg, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px 16px', cursor: 'pointer', textAlign: 'left', width: '100%' }}
          >
            <span style={{ fontSize: '20px' }}>🖼</span>
            <div>
              <div style={{ fontFamily: fs, fontSize: '14px', color: C.brown, fontWeight: '600' }}>Add from Camera Roll</div>
              <div style={{ fontFamily: fs, fontSize: '11px', color: C.muted, marginTop: '1px' }}>Use a photo you already have</div>
            </div>
          </button>
          <button
            onClick={() => setShowAdd(true)}
            style={{ display: 'flex', alignItems: 'center', gap: '12px', background: C.bg, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px 16px', cursor: 'pointer', textAlign: 'left', width: '100%' }}
          >
            <span style={{ fontSize: '20px', lineHeight: 1 }}>+</span>
            <div>
              <div style={{ fontFamily: fs, fontSize: '14px', color: C.brown, fontWeight: '600' }}>Add Item</div>
              <div style={{ fontFamily: fs, fontSize: '11px', color: C.muted, marginTop: '1px' }}>Enter details manually</div>
            </div>
          </button>
        </div>
      )}

      {/* Camera Roll chooser — appears when the Add from Camera Roll tile is
          tapped. The user picks whether their existing photo is a room shot
          or an item close-up, then the relevant flow opens with the library
          picker auto-launching. */}
      {showCameraRollChooser && (
        <div
          onClick={() => setShowCameraRollChooser(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(44,29,20,0.5)',
            zIndex: 1700, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '24px',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: C.card, borderRadius: '14px', padding: '22px', maxWidth: '360px', width: '100%' }}
          >
            <div style={{ fontFamily: f, fontSize: '17px', fontWeight: '700', color: C.brown, marginBottom: '6px' }}>
              What kind of photo?
            </div>
            <div style={{ fontFamily: fs, fontSize: '13px', color: C.muted, marginBottom: '18px', lineHeight: 1.5 }}>
              Tell us what's in the photo so we can read it the right way.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <button
                onClick={() => {
                  setShowCameraRollChooser(false)
                  setRoomPhotoSource('library')
                  setShowRoomPhotoCapture(true)
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                  background: '#FBF3E8', border: `1px solid ${C.border}`, borderRadius: '10px',
                  padding: '12px 14px', fontFamily: fs, fontSize: '14px',
                  color: C.brown, fontWeight: '600', cursor: 'pointer', textAlign: 'left',
                }}
              >
                <span style={{ fontSize: '20px' }}>📷</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '700' }}>Room Photo</div>
                  <div style={{ fontSize: '11px', color: C.muted, fontWeight: '400', marginTop: '1px' }}>A wide shot of the room</div>
                </div>
              </button>
              <button
                onClick={() => {
                  setShowCameraRollChooser(false)
                  setScanItemSource('library')
                  setShowScanItem(true)
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                  background: '#FBF3E8', border: `1px solid ${C.border}`, borderRadius: '10px',
                  padding: '12px 14px', fontFamily: fs, fontSize: '14px',
                  color: C.brown, fontWeight: '600', cursor: 'pointer', textAlign: 'left',
                }}
              >
                <span style={{ fontSize: '20px' }}>🔍</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '700' }}>Item Photo</div>
                  <div style={{ fontSize: '11px', color: C.muted, fontWeight: '400', marginTop: '1px' }}>A close-up of one thing</div>
                </div>
              </button>
              <button
                onClick={() => setShowCameraRollChooser(false)}
                style={{
                  background: 'transparent', color: C.muted,
                  border: `1px solid ${C.border}`, borderRadius: '10px',
                  padding: '11px', fontFamily: fs, fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showRoomPhotoCapture && (
        <RoomPhotoCapture
          room={room}
          homeId={homeId}
          sourcePreference={roomPhotoSource}
          onClose={() => setShowRoomPhotoCapture(false)}
        />
      )}

      {showScanRoom && (
        <ScanRoom
          room={room}
          homeId={homeId}
          onClose={() => setShowScanRoom(false)}
          onSaved={() => { setShowScanRoom(false); loadItems() }}
        />
      )}

      {showScanItem && (
        <ScanItem
          room={room}
          homeId={homeId}
          sourcePreference={scanItemSource}
          onClose={() => setShowScanItem(false)}
          onSaved={() => { setShowScanItem(false); loadItems() }}
        />
      )}

      {items.map(item => (
        <div
          key={item.id}
          role="button"
          tabIndex={0}
          onClick={() => openEdit(item)}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openEdit(item) } }}
          style={{ background: C.card, borderRadius: '10px', padding: '14px 16px', marginBottom: '8px', border: `1px solid ${C.border}`, cursor: 'pointer' }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontFamily: f, fontSize: '15px', color: C.brown }}>{item.name}</div>
              <div style={{ fontSize: '12px', color: C.muted, fontFamily: fs, marginTop: '2px' }}>{[item.brand, item.category].filter(Boolean).join(' · ')}</div>
              {item.purchase_price && (
                <div style={{ fontSize: '12px', color: C.muted, fontFamily: fs, marginTop: '2px' }}>
                  Purchased: ${parseFloat(item.purchase_price).toLocaleString()}{item.purchase_date ? ` · ${new Date(item.purchase_date).getFullYear()}` : ''}
                </div>
              )}
              {item.appraised_value && (
                <div style={{ fontSize: '12px', color: C.amber, fontFamily: fs, marginTop: '2px' }}>
                  Appraised: ${parseFloat(item.appraised_value).toLocaleString()}{item.appraisal_date ? ` (${new Date(item.appraisal_date).getFullYear()})` : ''}
                </div>
              )}
            </div>
            {/* Delete still nested inside the clickable card — stopPropagation
                so the tap doesn't also fire openEdit. The Edit button is
                gone; the whole card is now the affordance. */}
            <button
              onClick={e => { e.stopPropagation(); handleDelete(item.id) }}
              aria-label="Delete item"
              style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: '16px', padding: '4px 8px' }}
            >
              ✕
            </button>
          </div>
        </div>
      ))}

      {showAdd && (
        <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: '12px', padding: '16px', marginTop: '8px' }}>
          <div style={{ fontFamily: f, fontSize: '15px', color: C.brown, marginBottom: '14px' }}>{editingItem ? 'Edit Item' : 'Add Item'}</div>

          {/* Photo upload */}
          <div style={{ marginBottom: '14px' }}>
            <div style={{ fontSize: '10px', color: C.muted, letterSpacing: '1.5px', textTransform: 'uppercase', fontFamily: fs, marginBottom: '8px' }}>Photos ({photos.length}/5)</div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {photos.map((photo, i) => (
                <div key={i} style={{ position: 'relative' }}>
                  <img src={photo} alt="Item" style={{ width: 70, height: 70, objectFit: 'cover', borderRadius: '8px' }} />
                  <button onClick={() => setPhotos(p => p.filter((_, idx) => idx !== i))} style={{ position: 'absolute', top: -6, right: -6, background: C.danger, color: '#fff', border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', fontSize: '12px', lineHeight: 1, padding: 0 }}>×</button>
                </div>
              ))}
              {photos.length < 5 && (
                <label style={{ width: 70, height: 70, background: C.card, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '24px', border: `1px dashed ${C.border}` }}>
                  📷<input type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoUpload} />
                </label>
              )}
            </div>
          </div>

          {/* Receipt upload */}
          <div style={{ marginBottom: '14px' }}>
            <div style={{ fontSize: '10px', color: C.muted, letterSpacing: '1.5px', textTransform: 'uppercase', fontFamily: fs, marginBottom: '6px' }}>Receipt or Document</div>
            {receipt ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>🧾</span>
                <span style={{ fontSize: '13px', color: C.brown, fontFamily: fs }}>Receipt saved</span>
                <button onClick={() => setReceipt(null)} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontFamily: fs, fontSize: '13px' }}>Remove</button>
              </div>
            ) : (
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', background: C.card, borderRadius: '8px', cursor: 'pointer', fontSize: '13px', color: C.muted, fontFamily: fs, border: `1px dashed ${C.border}` }}>
                🧾 Upload receipt or document
                <input type="file" accept=".pdf,image/*" style={{ display: 'none' }} onChange={handleReceiptUpload} />
              </label>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={lbl}>Name *</label>
              <input style={inp} value={form.name} onChange={e => setField('name', e.target.value)} placeholder="Item name" autoFocus />
            </div>
            <div>
              <label style={lbl}>Category</label>
              <select style={{ ...inp, appearance: 'none' }} value={form.category} onChange={e => setField('category', e.target.value)}>
                <option value="">Select...</option>
                {INVENTORY_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div><label style={lbl}>Brand / Make</label><input style={inp} value={form.brand} onChange={e => setField('brand', e.target.value)} placeholder="Brand" /></div>
            <div><label style={lbl}>Model Number</label><input style={inp} value={form.model_number} onChange={e => setField('model_number', e.target.value)} placeholder="Model #" /></div>
            <div><label style={lbl}>Serial Number</label><input style={inp} value={form.serial_number} onChange={e => setField('serial_number', e.target.value)} placeholder="Serial #" /></div>
            <div><label style={lbl}>Manufactured</label><input style={inp} value={form.manufactured} onChange={e => setField('manufactured', e.target.value)} placeholder="06/2021" /></div>
            <div><label style={lbl}>Purchase Date</label><input style={inp} type="date" value={form.purchase_date} onChange={e => setField('purchase_date', e.target.value)} /></div>
            <div><label style={lbl}>Purchase Price ($)</label><input style={inp} type="number" value={form.purchase_price} onChange={e => setField('purchase_price', e.target.value)} placeholder="What you paid" /></div>
            <div><label style={lbl}>Appraised Value ($)</label><input style={inp} type="number" value={form.appraised_value} onChange={e => setField('appraised_value', e.target.value)} placeholder="Professional appraisal only" /></div>
            <div><label style={lbl}>Appraised By</label><input style={inp} value={form.appraised_by} onChange={e => setField('appraised_by', e.target.value)} placeholder="Appraiser name" /></div>
            <div><label style={lbl}>Appraisal Date</label><input style={inp} type="date" value={form.appraisal_date} onChange={e => setField('appraisal_date', e.target.value)} /></div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={lbl}>Notes</label>
              <input style={inp} value={form.notes} onChange={e => setField('notes', e.target.value)} placeholder="Condition, provenance, etc." />
            </div>
          </div>

          {/* Detail Photos with AI extraction (Edit mode only) */}
          {editingItem && (
            <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: `1px solid ${C.border}` }}>
              <style>{EDIT_DETAIL_SPIN_STYLE}</style>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '8px' }}>
                <div style={{ fontSize: '10px', color: C.muted, letterSpacing: '2px', textTransform: 'uppercase', fontFamily: fs, fontWeight: '700' }}>
                  Detail Photos
                </div>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontFamily: fs, fontSize: '11px', color: C.muted, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={editExtractEnabled}
                    onChange={e => setEditExtractEnabled(e.target.checked)}
                    style={{ margin: 0 }}
                  />
                  Read details from new photos
                </label>
              </div>
              <div style={{ fontFamily: fs, fontSize: '12px', color: C.muted, marginBottom: '10px', lineHeight: 1.5 }}>
                Add a photo of the serial label, data plate, paperwork, or anything that proves identity. Casavio will read any new fields it can.
              </div>

              {editPhotoUrls.length > 0 && (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
                  {editPhotoUrls.map((url, idx) => {
                    const ex = editDetailExtractions[url]
                    return (
                      <div key={url + idx} style={{ position: 'relative' }}>
                        <img src={url} alt="" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: '8px', background: C.bg }} />
                        {ex?.state === 'extracting' && (
                          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.25)', borderRadius: '8px' }}>
                            <Loader size={20} color="#fff" strokeWidth={2} style={{ animation: 'roomdetail-edit-spin 1s linear infinite' }} />
                          </div>
                        )}
                        {ex?.state === 'success' && (
                          <div style={{ position: 'absolute', bottom: 2, right: 2, width: 18, height: 18, borderRadius: '50%', background: C.success, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Check size={12} color="#fff" strokeWidth={3} />
                          </div>
                        )}
                        {ex?.state === 'error' && (
                          <div style={{ position: 'absolute', bottom: 2, right: 2, width: 18, height: 18, borderRadius: '50%', background: C.danger, color: '#fff', fontSize: '11px', fontWeight: '700', lineHeight: '18px', textAlign: 'center', fontFamily: fs }}>!</div>
                        )}
                        <button
                          onClick={() => removeEditDetailPhoto(idx)}
                          aria-label="Remove photo"
                          style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', background: '#fff', border: `1px solid ${C.border}`, cursor: 'pointer', padding: 0, lineHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          <X size={11} color={C.muted} />
                        </button>
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Success summary pills for newly-extracted fields */}
              {Object.entries(editDetailExtractions).filter(([, ex]) => ex.state === 'success' && ex.summary).map(([url, ex]) => (
                <div key={`pill-${url}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#E8F3EC', color: C.success, padding: '4px 10px', borderRadius: '999px', fontFamily: fs, fontSize: '11px', fontWeight: '600', marginRight: '6px', marginBottom: '8px' }}>
                  <Check size={12} strokeWidth={3} /> {ex.summary}
                </div>
              ))}

              <input
                ref={editDetailCameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: 'none' }}
                onChange={handleEditDetailPhotoSelect}
              />
              <input
                ref={editDetailLibraryRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleEditDetailPhotoSelect}
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <button
                  onClick={() => editDetailCameraRef.current?.click()}
                  disabled={editPhotoUploading || editPhotoUrls.length >= EDIT_DETAIL_PHOTOS_MAX}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'flex-start',
                    background: (editPhotoUploading || editPhotoUrls.length >= EDIT_DETAIL_PHOTOS_MAX) ? C.bg : `linear-gradient(180deg, ${C.amberLight} 0%, ${C.amber} 100%)`,
                    color: (editPhotoUploading || editPhotoUrls.length >= EDIT_DETAIL_PHOTOS_MAX) ? C.muted : '#fff',
                    border: 'none', borderRadius: '10px',
                    padding: '12px 14px', fontFamily: f, fontSize: '14px', fontWeight: '700',
                    cursor: (editPhotoUploading || editPhotoUrls.length >= EDIT_DETAIL_PHOTOS_MAX) ? 'not-allowed' : 'pointer',
                    opacity: (editPhotoUploading || editPhotoUrls.length >= EDIT_DETAIL_PHOTOS_MAX) ? 0.7 : 1,
                  }}
                >
                  <Camera size={16} strokeWidth={2} color={(editPhotoUploading || editPhotoUrls.length >= EDIT_DETAIL_PHOTOS_MAX) ? C.muted : '#fff'} />
                  {editPhotoUploading ? 'Uploading…' : 'Take a Photo'}
                </button>
                <button
                  onClick={() => editDetailLibraryRef.current?.click()}
                  disabled={editPhotoUploading || editPhotoUrls.length >= EDIT_DETAIL_PHOTOS_MAX}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'flex-start',
                    background: '#fff', color: C.brown,
                    border: `1px solid ${C.border}`, borderRadius: '10px',
                    padding: '12px 14px', fontFamily: fs, fontSize: '14px', fontWeight: '600',
                    cursor: (editPhotoUploading || editPhotoUrls.length >= EDIT_DETAIL_PHOTOS_MAX) ? 'not-allowed' : 'pointer',
                    opacity: (editPhotoUploading || editPhotoUrls.length >= EDIT_DETAIL_PHOTOS_MAX) ? 0.5 : 1,
                  }}
                >
                  <ImageIcon size={16} strokeWidth={2} color={C.brown} />
                  Add from Camera Roll
                </button>
                {editPhotoUrls.length >= EDIT_DETAIL_PHOTOS_MAX && (
                  <div style={{ fontFamily: fs, fontSize: '12px', color: C.muted }}>
                    Limit reached — {EDIT_DETAIL_PHOTOS_MAX} photos per item.
                  </div>
                )}
                {editPhotoError && (
                  <div style={{ fontFamily: fs, fontSize: '12px', color: C.danger }}>
                    {editPhotoError}
                  </div>
                )}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
            <button onClick={() => { setShowAdd(false); setEditingItem(null); setPhotos([]); setReceipt(null); setEditPhotoUrls([]); setEditDetailExtractions({}); setEditPhotoError(null); setEditExtractEnabled(true) }} style={{ flex: 1, background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px', fontSize: '13px', color: C.muted, cursor: 'pointer', fontFamily: fs }}>Cancel</button>
            <button onClick={handleSave} disabled={saving || !form.name.trim()} style={{ flex: 2, background: saving || !form.name.trim() ? C.border : C.amber, border: 'none', borderRadius: '8px', padding: '10px', fontSize: '13px', color: '#fff', cursor: 'pointer', fontFamily: fs, fontWeight: '700' }}>
              {saving ? 'Saving...' : 'Save Item'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

const MAX_VIDEOS_PER_ROOM = 3
const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/x-m4v']

function RoomVideoSection({ room, homeId, onRoomUpdate, onDone }) {
  const [videos, setVideos] = useState(() => Array.isArray(room.video_urls) ? room.video_urls : [])
  const [recording, setRecording] = useState(false)
  const [recordingTime, setRecordingTime] = useState(0)
  const [mediaRecorder, setMediaRecorder] = useState(null)
  const [chunks, setChunks] = useState([])
  const [previewUrl, setPreviewUrl] = useState(null)
  const [showGuide, setShowGuide] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploadError, setUploadError] = useState(null)
  const [activeStream, setActiveStream] = useState(null)
  const videoRef = useRef(null)
  const streamRef = useRef(null)
  const timerRef = useRef(null)

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`

  useEffect(() => {
    if (videoRef.current && activeStream) {
      videoRef.current.srcObject = activeStream
    }
  }, [activeStream])

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: true,
      })
      streamRef.current = stream
      const mimeType = MediaRecorder.isTypeSupported('video/mp4') ? 'video/mp4' : 'video/webm'
      const recorder = new MediaRecorder(stream, { mimeType })
      const recordedChunks = []
      recorder.ondataavailable = (e) => { if (e.data.size > 0) recordedChunks.push(e.data) }
      recorder.onstop = () => {
        const blob = new Blob(recordedChunks, { type: mimeType })
        setPreviewUrl(URL.createObjectURL(blob))
        setChunks(recordedChunks)
        if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop())
        setActiveStream(null)
      }
      recorder.start()
      setMediaRecorder(recorder)
      setRecording(true)
      setActiveStream(stream)
      setRecordingTime(0)
      timerRef.current = setInterval(() => {
        setRecordingTime(t => {
          if (t >= 299) { recorder.stop(); clearInterval(timerRef.current); setRecording(false); return 300 }
          return t + 1
        })
      }, 1000)
    } catch (err) {
      alert('Camera access required to record video. Please allow camera permissions.')
    }
  }

  const stopRecording = () => {
    if (mediaRecorder && recording) { mediaRecorder.stop(); clearInterval(timerRef.current); setRecording(false) }
  }

  const saveVideo = async () => {
    if (!previewUrl || !chunks.length || videos.length >= MAX_VIDEOS_PER_ROOM) return
    setSaving(true)
    setUploadError(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const userId = session?.user?.id
      if (!userId) throw new Error('Not authenticated')
      const mimeType = chunks[0]?.type || 'video/mp4'
      const ext = mimeType.includes('mp4') ? 'mp4' : 'webm'
      const path = `${userId}/${homeId}/${room.id}/${Date.now()}.${ext}`
      const blob = new Blob(chunks, { type: mimeType })
      try { await supabase.storage.createBucket('room-videos', { public: false }) } catch {}
      const { error: upErr } = await supabase.storage
        .from('room-videos')
        .upload(path, blob, { contentType: mimeType, upsert: false })
      if (upErr) throw upErr
      recordProvenance({
        file: blob,
        storageBucket: 'room-videos',
        storagePath: path,
        resourceType: 'room',
        resourceId: room.id,
        homeId: homeId || null,
        userId,
      }).catch(() => {})
      const { data: signedData, error: signedErr } = await supabase.storage
        .from('room-videos')
        .createSignedUrl(path, 315360000)
      if (signedErr) throw signedErr
      URL.revokeObjectURL(previewUrl)
      const newVideo = {
        url: signedData.signedUrl,
        storage_path: path,
        source: 'recorded',
        recorded_at: new Date().toISOString(),
        size_bytes: blob.size,
        duration_seconds: recordingTime,
      }
      const updated = [...videos, newVideo]
      await supabase.from('rooms').update({ video_urls: updated }).eq('id', room.id)
      setVideos(updated)
      onRoomUpdate({ ...room, video_urls: updated })
      setPreviewUrl(null)
      setChunks([])
      setShowGuide(true)
      if (onDone) onDone()
    } catch (err) {
      console.error('Video upload failed:', err)
      setUploadError('Upload failed — your recording is still here. Tap Save to try again.')
    } finally {
      setSaving(false)
    }
  }

  const deleteVideo = async (i) => {
    const updated = videos.filter((_, idx) => idx !== i)
    await supabase.from('rooms').update({ video_urls: updated }).eq('id', room.id)
    setVideos(updated)
    onRoomUpdate({ ...room, video_urls: updated })
  }

  const handleUpload = (e) => {
    const file = e.target.files[0]
    if (!file) return
    if (!ALLOWED_VIDEO_TYPES.includes(file.type) && !file.name.match(/\.(mp4|mov|m4v)$/i)) { alert('Please upload MP4 or MOV files only'); return }
    if (file.size > 500 * 1024 * 1024) { alert('Video must be under 500MB'); return }
    const url = URL.createObjectURL(file)
    setPreviewUrl(url)
    e.target.value = ''
  }

  if (videos.length > 0 || !showGuide) {
    return (
      <div>
        {videos.map((v, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', background: C.bg, borderRadius: '8px', padding: '12px', marginBottom: '8px' }}>
            <span style={{ fontSize: '24px' }}>🎬</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '13px', color: C.brown, fontFamily: fs }}>Room walkthrough</div>
              <div style={{ fontSize: '11px', color: C.muted, fontFamily: fs }}>{new Date(v.recorded_at).toLocaleDateString()}</div>
            </div>
            <a href={v.url} target="_blank" rel="noreferrer" style={{ background: C.amber, color: '#fff', border: 'none', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '13px', textDecoration: 'none', fontFamily: fs }}>▶ Play</a>
            <button onClick={() => deleteVideo(i)} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: '16px' }}>✕</button>
          </div>
        ))}
        {previewUrl && (
          <div style={{ marginBottom: '16px' }}>
            <video src={previewUrl} controls style={{ width: '100%', borderRadius: '10px', marginBottom: '8px' }} />
            {uploadError && (
              <div style={{ background: '#FDECEA', borderRadius: '8px', padding: '10px 14px', marginBottom: '8px', fontSize: '12px', color: '#B03020', fontFamily: fs }}>
                {uploadError}
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={saveVideo} disabled={saving} style={{ flex: 1, background: saving ? C.border : C.amber, color: saving ? C.muted : '#fff', border: 'none', borderRadius: '8px', padding: '12px 0', fontFamily: f, fontSize: '15px', cursor: saving ? 'not-allowed' : 'pointer' }}>{saving ? 'Uploading…' : '✓ Save Video'}</button>
              <button onClick={() => { setPreviewUrl(null); setChunks([]); setUploadError(null); setShowGuide(true) }} disabled={saving} style={{ flex: 1, background: 'none', border: `1px solid ${C.border}`, borderRadius: '8px', padding: '12px 0', fontSize: '15px', cursor: saving ? 'not-allowed' : 'pointer', color: C.muted, fontFamily: fs }}>Re-record</button>
            </div>
          </div>
        )}
        {!previewUrl && videos.length < MAX_VIDEOS_PER_ROOM && (
          <div style={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
            {!recording ? (
              <button onClick={startRecording} style={{ width: '100%', background: '#DC2626', color: '#fff', border: 'none', borderRadius: '8px', padding: '12px 0', fontFamily: f, fontSize: '15px', cursor: 'pointer' }}>● Record Room Video</button>
            ) : (
              <div>
                <video ref={videoRef} autoPlay playsInline muted style={{ width: '100%', borderRadius: '8px', background: '#000', marginBottom: '8px' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ color: '#DC2626', fontFamily: fs, fontSize: '13px' }}>● {formatTime(recordingTime)} / 5:00</span>
                  <button onClick={stopRecording} style={{ background: C.brown, color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 20px', fontFamily: f, fontSize: '14px', cursor: 'pointer' }}>■ Stop</button>
                </div>
              </div>
            )}
            <div style={{ textAlign: 'center' }}>
              <label style={{ fontSize: '13px', color: C.muted, cursor: 'pointer', fontFamily: fs }}>
                Or <span style={{ color: C.amber, textDecoration: 'underline' }}>upload an existing video</span>
                <input type="file" accept="video/mp4,video/quicktime,.mov,.mp4" style={{ display: 'none' }} onChange={handleUpload} />
              </label>
              <div style={{ fontSize: '11px', color: C.muted, marginTop: '4px', fontFamily: fs }}>MP4 or MOV · Max 500MB · Max 5 min</div>
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div>
      <div style={{ background: C.bg, borderRadius: '10px', padding: '16px', marginBottom: '16px' }}>
        <div style={{ fontSize: '14px', color: C.brown, fontWeight: '600', marginBottom: '10px', fontFamily: fs }}>Tips for a great inventory video:</div>
        {['• Walk slowly around the entire room', '• Open drawers and closets briefly', '• Narrate what you see out loud', '• Pause on valuable items', '• Include serial numbers if visible', '• Maximum 5 minutes'].map(tip => (
          <div key={tip} style={{ fontSize: '13px', color: C.muted, marginBottom: '4px', fontFamily: fs }}>{tip}</div>
        ))}
      </div>
      <div style={{ fontSize: '12px', color: C.muted, marginBottom: '16px', fontFamily: fs }}>Video is stored locally for insurance documentation. Never shared with contractors.</div>
      <button onClick={() => setShowGuide(false)} style={{ width: '100%', background: C.amber, color: '#fff', border: 'none', borderRadius: '8px', padding: '14px 0', fontFamily: f, fontSize: '16px', cursor: 'pointer', marginBottom: '12px' }}>
        📹 Record Room Video
      </button>
      <div style={{ textAlign: 'center' }}>
        <label style={{ fontSize: '13px', color: C.muted, cursor: 'pointer', fontFamily: fs }}>
          Or <span style={{ color: C.amber, textDecoration: 'underline' }}>upload an existing video</span>
          <input type="file" accept="video/mp4,video/quicktime,.mov,.mp4" style={{ display: 'none' }} onChange={handleUpload} />
        </label>
        <div style={{ fontSize: '11px', color: C.muted, marginTop: '4px', fontFamily: fs }}>MP4 or MOV · Max 500MB · Max 5 min · {MAX_VIDEOS_PER_ROOM} videos per room</div>
      </div>
    </div>
  )
}

const ROOM_TILES = [
  { id: 'paint', emoji: '🎨', label: 'Paint',
    summary: r => r.wall_paint_color || r.wall_paint_brand ? `${r.wall_paint_brand || ''} ${r.wall_paint_color || ''}`.trim() : 'Tap to add' },
  { id: 'flooring', emoji: '🪵', label: 'Flooring',
    summary: r => r.flooring_type ? r.flooring_type.replace(/_/g, ' ') : 'Tap to add' },
  { id: 'dimensions', emoji: '📐', label: 'Dimensions',
    summary: r => r.dimension_length && r.dimension_width ? `${r.dimension_length}×${r.dimension_width}ft` : 'Tap to add' },
  { id: 'inventory', emoji: '📦', label: 'Inventory',
    summary: () => 'Tap to view' },
  { id: 'photos', emoji: '🏞', label: 'Room Photos',
    summary: () => 'Tap to view' },
  { id: 'fixtures', emoji: '💡', label: 'Fixtures',
    summary: r => r.fixtures_notes ? r.fixtures_notes.substring(0, 28) + (r.fixtures_notes.length > 28 ? '…' : '') : 'Tap to add' },
  { id: 'windows', emoji: '🪟', label: 'Windows',
    summary: r => r.windows_notes ? r.windows_notes.substring(0, 28) + (r.windows_notes.length > 28 ? '…' : '') : 'Tap to add' },
  { id: 'notes', emoji: '📝', label: 'Notes',
    summary: r => r.room_notes ? r.room_notes.substring(0, 28) + (r.room_notes.length > 28 ? '…' : '') : 'Tap to add' },
]

// Grid of all room_photo_canvas captures for a single room. Newest first.
// Tap a thumbnail to open the display-only RoomPhotoViewer. Next session
// will layer pinch-to-zoom + "show what Casavio noticed" overlay + tap-a-
// region gestures on top of the viewer.
function RoomPhotosSection({ room }) {
  const [photos, setPhotos] = useState([])
  const [loading, setLoading] = useState(true)
  const [viewerUrl, setViewerUrl] = useState(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      const { data, error } = await supabase
        .from('room_scan_sessions')
        .select('id, source_photo_urls, created_at')
        .eq('room_id', room.id)
        .eq('capture_mode', 'room_photo_canvas')
        .order('created_at', { ascending: false })
      if (cancelled) return
      if (error) {
        console.error('[RoomPhotosSection] load error', error)
        setPhotos([])
      } else {
        // Each session has exactly one source photo (room_photo_canvas
        // sets frame_count=1). Use the first URL; skip rows with none.
        const list = (data || [])
          .map(row => ({ id: row.id, url: row.source_photo_urls?.[0] || null }))
          .filter(p => !!p.url)
        setPhotos(list)
      }
      setLoading(false)
    }
    load()
    return () => { cancelled = true }
  }, [room.id])

  if (loading) {
    return <div style={{ color: C.muted, fontFamily: fs, fontSize: '13px', padding: '20px 0' }}>Loading…</div>
  }

  if (photos.length === 0) {
    return (
      <div style={{ color: C.muted, fontFamily: fs, fontSize: '13px', padding: '20px 0', textAlign: 'center', lineHeight: 1.55 }}>
        No room photos yet.<br />
        Tap Inventory → Room Photo to capture this room.
      </div>
    )
  }

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '8px', paddingBottom: '20px' }}>
        {photos.map(p => (
          <button
            key={p.id}
            onClick={() => setViewerUrl(p.url)}
            aria-label="Open photo"
            style={{
              padding: 0, border: 'none', background: 'none',
              cursor: 'pointer', borderRadius: '8px', overflow: 'hidden',
              aspectRatio: '1 / 1',
            }}
          >
            <img
              src={p.url}
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', background: C.bg }}
            />
          </button>
        ))}
      </div>
      {viewerUrl && (
        <RoomPhotoViewer photoUrl={viewerUrl} onClose={() => setViewerUrl(null)} alt="Room photo" />
      )}
    </>
  )
}

export default function RoomDetail({ room: initialRoom, homeId, onSave, onClose }) {
  const [room, setRoom] = useState({ ...initialRoom })
  const [activeRoomTile, setActiveRoomTile] = useState(null)
  const [saving, setSaving] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const updateRoom = (field, value) => {
    const updated = { ...room, [field]: value }
    if (field === 'dimension_length' || field === 'dimension_width') {
      const l = field === 'dimension_length' ? value : room.dimension_length
      const w = field === 'dimension_width' ? value : room.dimension_width
      if (l && w) updated.sq_footage = Math.round(parseFloat(l) * parseFloat(w))
    }
    setRoom(updated)
  }

  const handleSave = async () => {
    setSaving(true)
    const { error } = await supabase.from('rooms').update({
      name: room.name,
      type: room.type,
      floor_level: room.floor_level,
      wall_paint_brand: room.wall_paint_brand || null,
      wall_paint_color: room.wall_paint_color || null,
      wall_paint_code: room.wall_paint_code || null,
      wall_paint_finish: room.wall_paint_finish || null,
      trim_paint_brand: room.trim_paint_brand || null,
      trim_paint_color: room.trim_paint_color || null,
      trim_paint_code: room.trim_paint_code || null,
      ceiling_paint_brand: room.ceiling_paint_brand || null,
      ceiling_paint_color: room.ceiling_paint_color || null,
      flooring_type: room.flooring_type || null,
      flooring_brand: room.flooring_brand || null,
      flooring_color: room.flooring_color || null,
      flooring_model: room.flooring_model || null,
      flooring_year_installed: room.flooring_year_installed ? parseInt(room.flooring_year_installed) : null,
      dimension_length: room.dimension_length ? parseFloat(room.dimension_length) : null,
      dimension_width: room.dimension_width ? parseFloat(room.dimension_width) : null,
      sq_footage: room.sq_footage ? parseFloat(room.sq_footage) : null,
      fixtures_notes: room.fixtures_notes || null,
      windows_notes: room.windows_notes || null,
      notes: room.notes || null,
      room_notes: room.room_notes || null,
    }).eq('id', room.id)
    setSaving(false)
    if (!error) {
      setActiveRoomTile(null)
      onSave({ ...room })
    }
  }

  const handleDelete = async () => {
    setDeleting(true)
    await supabase.from('rooms').delete().eq('id', room.id)
    onSave(null)
  }

  const tileSectionProps = { room, updateRoom, saving, onSave: handleSave }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(44,29,20,0.55)', zIndex: 1100, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
      onClick={e => e.target === e.currentTarget && (activeRoomTile ? setActiveRoomTile(null) : onClose())}
    >
      <div style={{ width: '100%', maxWidth: '560px', maxHeight: '92vh', background: C.card, borderRadius: '20px 20px 0 0', border: `1px solid ${C.border}`, borderBottom: 'none', boxShadow: '0 -8px 40px rgba(44,29,20,0.2)', display: 'flex', flexDirection: 'column', fontFamily: f }}>

        {/* Drag handle */}
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '12px', paddingBottom: '4px', flexShrink: 0 }}>
          <div style={{ width: '40px', height: '4px', borderRadius: '2px', background: C.border }} />
        </div>

        {/* Header */}
        <div style={{ padding: '8px 24px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          {activeRoomTile ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button onClick={() => setActiveRoomTile(null)} style={{ background: 'none', border: 'none', color: C.amber, cursor: 'pointer', fontSize: '14px', padding: 0, fontFamily: fs }}>← Back</button>
              <div style={{ fontSize: '16px', fontWeight: '700', color: C.brown }}>
                {ROOM_TILES.find(t => t.id === activeRoomTile)?.emoji} {ROOM_TILES.find(t => t.id === activeRoomTile)?.label}
              </div>
            </div>
          ) : (
            <input
              style={{ fontSize: '20px', fontWeight: '700', color: C.brown, background: 'none', border: 'none', outline: 'none', fontFamily: f, flex: 1 }}
              value={room.name}
              onChange={e => updateRoom('name', e.target.value)}
            />
          )}
          <button onClick={() => activeRoomTile ? setActiveRoomTile(null) : onClose()} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: C.muted, padding: 0, lineHeight: 1 }}>×</button>
        </div>

        {/* Scrollable body */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '16px 24px 32px' }}>

          {!activeRoomTile && (
            <>
              {/* Room type + floor */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                <div>
                  <label style={lbl}>Room Type</label>
                  <select style={{ ...inp, appearance: 'none', cursor: 'pointer' }} value={room.type || ''} onChange={e => updateRoom('type', e.target.value)}>
                    <option value="">Select...</option>
                    {ROOM_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <label style={lbl}>Floor Level</label>
                  <select style={{ ...inp, appearance: 'none', cursor: 'pointer' }} value={room.floor_level ?? 1} onChange={e => updateRoom('floor_level', parseInt(e.target.value))}>
                    {FLOOR_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>

              {/* Tile grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '10px', marginBottom: '20px' }}>
                {ROOM_TILES.map(tile => (
                  <div
                    key={tile.id}
                    onClick={() => setActiveRoomTile(tile.id)}
                    style={{ background: C.bg, borderRadius: '12px', padding: '14px', cursor: 'pointer', textAlign: 'center', border: `1px solid ${C.border}`, transition: 'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#EDE6DA'}
                    onMouseLeave={e => e.currentTarget.style.background = C.bg}
                  >
                    <div style={{ fontSize: '26px', marginBottom: '6px' }}>{tile.emoji}</div>
                    <div style={{ fontFamily: f, fontSize: '13px', color: C.brown, marginBottom: '4px' }}>{tile.label}</div>
                    <div style={{ fontSize: '11px', color: C.muted, fontFamily: fs, lineHeight: 1.3 }}>{tile.summary(room)}</div>
                  </div>
                ))}
              </div>

              {/* Delete */}
              {!confirmDelete ? (
                <button onClick={() => setConfirmDelete(true)} style={{ width: '100%', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '10px', padding: '11px', fontSize: '12px', color: C.muted, cursor: 'pointer', fontFamily: fs }}>
                  Delete Room
                </button>
              ) : (
                <div style={{ background: C.dangerBg, border: `1px solid ${C.danger}44`, borderRadius: '10px', padding: '14px', textAlign: 'center' }}>
                  <div style={{ fontSize: '13px', color: C.danger, fontFamily: fs, marginBottom: '10px' }}>Delete this room permanently?</div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button onClick={() => setConfirmDelete(false)} style={{ flex: 1, background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px', fontSize: '13px', color: C.muted, cursor: 'pointer', fontFamily: fs }}>Cancel</button>
                    <button onClick={handleDelete} disabled={deleting} style={{ flex: 1, background: C.danger, border: 'none', borderRadius: '8px', padding: '8px', fontSize: '13px', color: '#fff', cursor: 'pointer', fontFamily: fs, fontWeight: '700' }}>{deleting ? 'Deleting...' : 'Yes, Delete'}</button>
                  </div>
                </div>
              )}
            </>
          )}

          {activeRoomTile === 'paint' && <RoomPaintSection {...tileSectionProps} />}
          {activeRoomTile === 'flooring' && <RoomFlooringSection {...tileSectionProps} />}
          {activeRoomTile === 'dimensions' && <RoomDimensionsSection {...tileSectionProps} />}
          {activeRoomTile === 'inventory' && <RoomInventorySection room={room} homeId={homeId} onRoomUpdate={(updated) => { setRoom(updated); onSave(updated) }} />}
          {activeRoomTile === 'photos' && <RoomPhotosSection room={room} />}
          {activeRoomTile === 'fixtures' && <RoomFixturesSection {...tileSectionProps} />}
          {activeRoomTile === 'windows' && <RoomWindowsSection {...tileSectionProps} />}
          {activeRoomTile === 'notes' && <RoomNotesSection {...tileSectionProps} />}
        </div>
      </div>
    </div>
  )
}
